import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { getIDFromUrl } from '@rossum/api-client';
import { IconAlertOctagon } from '@rossum/ui/icons/tabler';
import { Stack, SvgIcon, Tooltip } from '@rossum/ui/material';
import { GridRenderCellParams } from '@rossum/ui/x-data-grid-pro';
import { constructDocumentUrl } from '../../../lib/url';
import { clearAnnotationData } from '../../../redux/modules/annotation/actions';
import { startValidation } from '../../../redux/modules/ui/actions';
import { TransformedAnnotation } from '../hooks/useTransformAnnotation';
import { Filename } from './Filename';
import { StyledLink } from './StyledLink';

type SimpleFilenameRowContext = Pick<
  TransformedAnnotation,
  'status' | 'pages' | 'id' | 'hasAttachmentRelation'
>;

type SimpleFilenameCellValue = TransformedAnnotation['original_file_name'];

export const SimpleFilename = ({
  value: originalFilename,
  row,
  shouldShowAttachmentWarning = false,
}: GridRenderCellParams<SimpleFilenameRowContext, SimpleFilenameCellValue> & {
  shouldShowAttachmentWarning?: boolean;
}) => {
  const intl = useIntl();
  const filenameActionsDisabled =
    row.status === 'failed_import' ||
    row.status === 'importing' ||
    row.pages.length === 0;

  const dispatch = useDispatch();
  const annotate = (url: string) => {
    dispatch(clearAnnotationData());
    return dispatch(startValidation(url, [getIDFromUrl(url)]));
  };
  const url = constructDocumentUrl({ id: row.id });

  return (
    <Stack direction="row" spacing={1} alignItems="center" height="100%">
      {shouldShowAttachmentWarning && row.hasAttachmentRelation ? (
        <Tooltip
          title={intl.formatMessage({
            id: 'components.modal.addAttachments.disabledAnnotation.tooltip',
          })}
        >
          <SvgIcon color="warning" fontSize="medium">
            <IconAlertOctagon />
          </SvgIcon>
        </Tooltip>
      ) : null}
      <Tooltip title={originalFilename}>
        <StyledLink
          to={url}
          sx={{
            pointerEvents: filenameActionsDisabled ? 'none' : 'inherit',
            maxWidth: 1,
          }}
          onClick={e => {
            if (!e.metaKey) {
              e.preventDefault();
              annotate(url);
            }
          }}
        >
          <Filename
            status={row.status}
            disabled={filenameActionsDisabled}
            originalFileName={originalFilename}
            disableClipboard
          />
        </StyledLink>
      </Tooltip>
    </Stack>
  );
};
